/* eslint-disable arrow-body-style */
/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useTable, useSortBy, usePagination, useRowSelect, useGlobalFilter } from 'react-table';
import { 
  HiOutlineChevronDown, 
  HiOutlineChevronUp, 
  HiOutlinePencilSquare, 
  HiOutlineEye,
  HiOutlineTrash,
  HiOutlineMagnifyingGlass,
  HiOutlineCommandLine
} from 'react-icons/hi2';
import { GoLinkExternal } from 'react-icons/go';
import Pagination from './TablePagination';
import Modal from '../Modal';
import './index.css';

const IndeterminateCheckbox = React.forwardRef(({ indeterminate, ...rest }, ref) => {
  const defaultRef = useRef();
  const resolvedRef = ref || defaultRef;

  useEffect(() => {
    resolvedRef.current.indeterminate = indeterminate;
  }, [resolvedRef, indeterminate]);

  return (
    <div className='flex items-center'>
      <input 
        type='checkbox' 
        ref={resolvedRef} 
        {...rest} 
        className='
          w-4 h-4 rounded border-gray-300 text-primary-600 
          focus:ring-primary-500 focus:ring-offset-0 
          dark:bg-gray-800 dark:border-gray-700
          cursor-pointer transition-all duration-200
        '
      />
    </div>
  );
});

const TableComponent = ({
  data,
  columns,
  onViewHandler,
  onEditHandler,
  onDeleteHandler,
  hideActions,
  hidePagination,
  emptyText,
  placeholder,
  defaultSortBy,
  loading,
  advancedSearchComponent,
  recordName,
  viewExternal,
  showSelection = false,
}) => {
  const [tableColumns, setTableColumns] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!hideActions) {
      const actionColumn = {
        Header: 'Actions',
        accessor: 'actions',
        disableSortBy: true,
        center: true,
        Cell: ({ row }) => (
          <div className='flex items-center justify-center gap-1.5'>
            {onViewHandler && (
              <button
                type='button'
                title='View'
                onClick={(e) => {
                  e.stopPropagation();
                  onViewHandler(e, row.original);
                }}
                className='p-2 rounded-lg text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-500/10 transition-colors'
              >
                {viewExternal ? <GoLinkExternal className='w-4 h-4' /> : <HiOutlineEye className='w-4 h-4' />}
              </button>
            )}
            {onEditHandler && (
              <button
                type='button'
                title='Edit'
                onClick={(e) => {
                  e.stopPropagation();
                  onEditHandler(e, row.original);
                }}
                className='p-2 rounded-lg text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-500/10 transition-colors'
              >
                <HiOutlinePencilSquare className='w-4 h-4' />
              </button>
            )}
            {onDeleteHandler && (
              <button
                type='button'
                title='Delete'
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteHandler(e, row.original);
                }}
                className='p-2 rounded-lg text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors'
              >
                <HiOutlineTrash className='w-4 h-4' />
              </button>
            )}
          </div>
        ),
      };
      setTableColumns([...columns, actionColumn]);
    } else {
      setTableColumns(columns);
    }
  }, [columns, hideActions, onViewHandler, onEditHandler, onDeleteHandler, viewExternal]);

  const modifiedColumns = useMemo(() => tableColumns, [tableColumns]);

  const tableHooks = [
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
  ];

  if (showSelection) {
    tableHooks.push((hooks) => {
      hooks.columns.push((cols) => [
        {
          id: 'selection',
          Header: ({ getToggleAllRowsSelectedProps }) => (
            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
          ),
          Cell: ({ row }) => (
            <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
          ),
          width: 40,
        },
        ...cols,
      ]);
    });
  }

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    selectedFlatRows,
    setGlobalFilter,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns: modifiedColumns,
      data,
      disableSortRemove: true,
      initialState: {
        pageIndex: 0,
        pageSize: 10,
        sortBy: [{ id: defaultSortBy ?? 'createdAt', desc: true }],
      },
    },
    ...tableHooks
  );

  const handleSearch = (e) => {
    const { value } = e.target;
    setSearchTerm(value);
    setGlobalFilter(value);
  };

  const [showModal, setShowModal] = useState(false);

  return (
    <div className='w-full'>
      <Modal size='sm' open={showModal} onClose={() => setShowModal(false)}>
        <div className='flex flex-col gap-2 p-4'>{advancedSearchComponent}</div>
      </Modal>

      {/* Table Controls */}
      <div className='p-4 border-b border-gray-100 dark:border-gray-800 space-y-4'>
        <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-4'>
          <div className='flex items-baseline gap-2'>
            <h2 className='text-2xl font-bold text-gray-900 dark:text-white'>
              {loading ? '...' : data?.length}
            </h2>
            <span className='text-sm font-medium text-gray-500 dark:text-gray-400 capitalize'>
              {recordName ?? 'Records'}
            </span>
            {selectedFlatRows.length > 0 && (
              <span className='px-2 py-0.5 text-[10px] font-bold bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 rounded-full uppercase tracking-wider animate-in fade-in zoom-in duration-300'>
                {selectedFlatRows.length} Selected
              </span>
            )}
          </div>

          <div className='flex items-center gap-2'>
            <div className='relative group'>
              <HiOutlineMagnifyingGlass className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-primary-500 transition-colors' />
              <input
                type='text'
                value={searchTerm}
                onChange={handleSearch}
                placeholder={placeholder ?? 'Search...'}
                className='
                  w-full sm:w-64 md:w-80 pl-9 pr-4 py-2 text-sm
                  bg-gray-50 dark:bg-gray-800/50 
                  border border-gray-200 dark:border-gray-700
                  rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500
                  transition-all duration-200
                '
              />
            </div>

            {advancedSearchComponent && (
              <button
                onClick={() => setShowModal(true)}
                type='button'
                className='
                  p-2 rounded-xl border border-gray-200 dark:border-gray-700
                  text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800
                  transition-colors duration-200
                '
                title='Advanced Search'
              >
                <HiOutlineCommandLine className='w-5 h-5' />
              </button>
            )}

            <div className='relative'>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className='
                  pl-3 pr-8 py-2 text-sm font-medium
                  bg-gray-50 dark:bg-gray-800/50 
                  border border-gray-200 dark:border-gray-700
                  rounded-xl appearance-none cursor-pointer
                  focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500
                  transition-all duration-200
                '
              >
                {[10, 20, 30, 50, 100].map((pSize) => (
                  <option key={pSize} value={pSize}>
                    {pSize} / Page
                  </option>
                ))}
              </select>
              <HiOutlineChevronDown className='absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none' />
            </div>
          </div>
        </div>
      </div>

      {/* Table Body */}
      <div className='table-wrapper border-none'>
        <table {...getTableProps()} className='w-full text-left border-separate border-spacing-0'>
          <thead>
            {headerGroups?.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th 
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className='
                      px-4 py-4 text-xs font-bold uppercase tracking-wider 
                      text-gray-500 dark:text-gray-400 bg-gray-50/50 dark:bg-gray-800/30
                      border-b border-gray-100 dark:border-gray-800
                      first:pl-6 last:pr-6
                    '
                  >
                    <div className={`flex items-center gap-2 ${column.center ? 'justify-center' : ''}`}>
                      {column.render('Header')}
                      {column.canSort && (
                        <div className='flex flex-col'>
                          <HiOutlineChevronUp className={`w-2.5 h-2.5 -mb-0.5 ${column.isSorted && !column.isSortedDesc ? 'text-primary-500' : 'text-gray-300 dark:text-gray-600'}`} />
                          <HiOutlineChevronDown className={`w-2.5 h-2.5 ${column.isSortedDesc ? 'text-primary-500' : 'text-gray-300 dark:text-gray-600'}`} />
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()} className='divide-y divide-gray-50 dark:divide-gray-800'>
            {loading && (
              /* eslint-disable react/no-array-index-key */
              [...Array(pageSize)].map((_, rowIndex) => (
                <tr key={`skeleton-row-${rowIndex}`} className='animate-pulse'>
                  {modifiedColumns.map((__, colIndex) => (
                    <td key={`skeleton-col-${colIndex}`} className='px-4 py-4'>
                      <div className='h-4 bg-gray-100 dark:bg-gray-800 rounded w-full opacity-50' />
                    </td>
                  ))}
                </tr>
              ))
              /* eslint-enable react/no-array-index-key */
            )}
            
            {!loading && page?.length > 0 && (
              page.map((row) => {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps()}
                    onClick={onViewHandler ? (e) => onViewHandler(e, row.original) : null}
                    className={`
                      group transition-colors duration-150
                      ${onViewHandler ? 'cursor-pointer hover:bg-gray-50/50 dark:hover:bg-primary-500/5' : ''}
                      ${row.isSelected ? 'bg-primary-50/30 dark:bg-primary-500/10' : ''}
                    `}
                  >
                    {row.cells.map((cell) => (
                      <td
                        {...cell.getCellProps()}
                        className={`
                          px-4 py-4 text-sm text-gray-600 dark:text-gray-300
                          first:pl-6 last:pr-6
                          ${cell.column?.className || ''}
                        `}
                        style={cell.column?.style}
                      >
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Empty State */}
        {!loading && page?.length === 0 && (
          <div className='flex flex-col items-center justify-center py-20 px-4'>
            <div className='w-16 h-16 bg-gray-50 dark:bg-gray-800 rounded-2xl flex items-center justify-center mb-4'>
              <HiOutlineMagnifyingGlass className='w-8 h-8 text-gray-300' />
            </div>
            <p className='text-gray-500 dark:text-gray-400 font-medium'>
              {searchTerm ? (
                <>No results found for <span className='text-gray-900 dark:text-white font-bold'>"{searchTerm}"</span></>
              ) : (
                emptyText ?? 'No records found'
              )}
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!hidePagination && data.length > 0 && (
        <Pagination
          pageIndex={pageIndex}
          pageOptions={pageOptions}
          gotoPage={gotoPage}
          previousPage={previousPage}
          canPreviousPage={canPreviousPage}
          nextPage={nextPage}
          canNextPage={canNextPage}
        />
      )}
    </div>
  );
};

export default TableComponent;
